import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

export default function Walkthrough({ steps, pageKey, onComplete }) {
  const [step, setStep] = useState(0);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [spot, setSpot] = useState(null);
  const done = useRef(false);

  // Always show walkthrough on every visit
  useEffect(() => {
    if (!done.current) {
      // If email already verified, show immediately
      if (localStorage.getItem('forsyt_email')) {
        setShow(true);
        return;
      }
      // Poll until email gate is passed
      const interval = setInterval(() => {
        if (localStorage.getItem('forsyt_email')) {
          setShow(true);
          clearInterval(interval);
        }
      }, 500);
      return () => clearInterval(interval);
    }
  }, [pageKey]);

  useEffect(() => {
    if (!show || !steps[step]) return;
    const reposition = () => {
      const el = document.querySelector(steps[step].target);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const pad = 6;
      setSpot({
        top: r.top - pad,
        left: r.left - pad,
        width: r.width + pad * 2,
        height: r.height + pad * 2,
      });
      const tw = 340, th = 200;
      let t = r.bottom + 12;
      let l = r.left + r.width / 2 - tw / 2;
      if (t + th > window.innerHeight) t = r.top - th - 12;
      if (t < 10) t = 10;
      if (t + th > window.innerHeight - 10) t = window.innerHeight - th - 10;
      if (l < 10) l = 10;
      if (l + tw > window.innerWidth - 10) l = window.innerWidth - tw - 10;
      setPos({ top: t, left: l });
    };
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [show, step, steps]);

  function finish() {
    done.current = true;
    localStorage.setItem(`walkthrough_${pageKey}`, 'true');
    setShow(false);
    setSpot(null);
    setStep(0);
    onComplete?.();
  }

  function goNext() {
    if (step >= steps.length - 1) finish();
    else setStep(step + 1);
  }

  function goPrev() {
    if (step > 0) setStep(step - 1);
  }

  if (!show || !steps.length) return null;

  const current = steps[step];
  const isLast = step >= steps.length - 1;

  return createPortal(
    <>
      {/* Backdrop, below email gate (z-200), above content */}
      <div className="fixed inset-0 z-[150] pointer-events-none" aria-hidden="true" data-testid="walkthrough-backdrop">
        <svg className="absolute inset-0 w-full h-full" style={{ display: 'block' }}>
          <defs>
            <mask id={`wt-${pageKey}`}>
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {spot && <rect x={spot.left} y={spot.top} width={spot.width} height={spot.height} rx="8" fill="black" />}
            </mask>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.45)" mask={`url(#wt-${pageKey})`} />
        </svg>
        {spot && (
          <div
            className="fixed border-2 border-blue-400 rounded-lg"
            style={{
              top: spot.top,
              left: spot.left,
              width: spot.width,
              height: spot.height,
              boxShadow: '0 0 0 4px rgba(59,130,246,0.15)',
            }}
            data-testid="walkthrough-spotlight"
          />
        )}
      </div>

      {/* Tooltip */}
      <div
        className="fixed bg-white rounded-xl shadow-2xl border border-gray-200 w-[340px] p-5 z-[151]"
        style={{ top: pos.top, left: pos.left, pointerEvents: 'auto' }}
        data-testid="walkthrough-tooltip"
      >
        <button onClick={finish} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600" data-testid="walkthrough-skip">
          <X className="w-4 h-4" />
        </button>
        <p className="text-[11px] font-bold text-blue-600 tracking-wider uppercase mb-2">
          Step {step + 1} of {steps.length}
        </p>
        <h3 className="text-base font-bold text-gray-900 mb-1.5">{current.title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-5">{current.description}</p>
        <div className="flex items-center justify-between">
          <button onClick={finish} className="text-xs font-medium text-gray-400 hover:text-gray-600" data-testid="walkthrough-dismiss">
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button onClick={goPrev} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900" data-testid="walkthrough-prev">
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
            <button onClick={goNext} className="flex items-center gap-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg" data-testid="walkthrough-next">
              {isLast ? 'Got it!' : (<>Next <ChevronRight className="w-3.5 h-3.5" /></>)}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
